import { useState } from 'react';
import {
  Check,
  Copy,
  ExternalLink,
  Info,
  Pencil,
  RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { useWorkspace } from '@/contexts/workspace-context';
import type { WhatsAppChannel } from '@/types/channel';
import { docsUrl } from '@/lib/docs-url';
import { midEllipsis, midEllipsisUrl } from '@/lib/mid-ellipsis';
import { AccessTokenField } from './access-token-field';
import { WhatsAppProfilePhoto } from './whatsapp-profile-photo';
import { singularUnit, unitTitle } from '@/lib/usage-unit';

// Module-scope (not nested in the component) so it isn't recreated each render
// — react-hooks/static-components. Pure props, no closure over component state.
function CopyButton({
  copied,
  onClick,
  label,
}: {
  copied: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <Button
      variant="ghost"
      size="icon-xs"
      onClick={onClick}
      aria-label={label}
      title={label}
    >
      {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
    </Button>
  );
}

export interface WhatsAppChannelBodyProps {
  // Narrowed to WhatsAppChannel — this component is WA-only; callers
  // must guard with channel.type === 'whatsapp' before passing.
  channel: WhatsAppChannel;
  canEdit: boolean;
  // Webhook-related state from parent (lifted to shell):
  currentWebhookUrl: string | null;
  currentVerifyToken: string | null;
  setShowUrlDialog: (open: boolean) => void;
  setUrlDialogMode: (mode: 'configure' | 'edit') => void;
  setSwitchToCliDialogOpen: (open: boolean) => void;
  // HMAC signing secret — SEPARATE from the verify token. Shown + rotatable
  // only when a webhook destination is configured.
  currentHmacSecret: string | null;
  onRotateHmac: () => void;
  rotatingHmac: boolean;
  // Refresh-driven state from parent (updated by on-mount auto-refresh):
  currentWabaName: string | null;
  currentBusinessName: string | null;
  currentPhoneNumberId: string | null;
  // AIT-713: after a new profile photo is set, re-read the channel so the stored avatar updates.
  onProfilePhotoChanged: () => void;
}

export function WhatsAppChannelBody({
  channel,
  canEdit,
  currentWebhookUrl,
  currentVerifyToken,
  setShowUrlDialog,
  setUrlDialogMode,
  setSwitchToCliDialogOpen,
  currentHmacSecret,
  onRotateHmac,
  rotatingHmac,
  currentWabaName,
  currentBusinessName,
  currentPhoneNumberId,
  onProfilePhotoChanged,
}: WhatsAppChannelBodyProps) {
  const { usage } = useWorkspace();
  const channelUsage = usage?.channels.find((a) => a.channelId === channel.id);

  const [copiedWaba, setCopiedWaba] = useState(false);
  const [copiedPhoneId, setCopiedPhoneId] = useState(false);
  const [copiedVerifyToken, setCopiedVerifyToken] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  async function handleCopyChannelId() {
    try {
      await navigator.clipboard.writeText(channel.id);
      setCopiedWaba(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopiedWaba(false), 2000);
    } catch {
      toast.error('Failed to copy. Please copy manually.');
    }
  }

  async function handleCopyPhoneId() {
    if (!currentPhoneNumberId) return;
    try {
      await navigator.clipboard.writeText(currentPhoneNumberId);
      setCopiedPhoneId(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopiedPhoneId(false), 2000);
    } catch {
      toast.error('Failed to copy. Please copy manually.');
    }
  }

  async function handleCopyVerifyToken() {
    if (!currentVerifyToken) return;
    try {
      await navigator.clipboard.writeText(currentVerifyToken);
      setCopiedVerifyToken(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopiedVerifyToken(false), 2000);
    } catch {
      toast.error('Failed to copy. Please copy manually.');
    }
  }

  async function handleCopyField(value: string, fieldName: string) {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(fieldName);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      toast.error('Failed to copy. Please copy manually.');
    }
  }

  function handleEditUrl() {
    setUrlDialogMode('edit');
    setShowUrlDialog(true);
  }

  function handleSetUrl() {
    setUrlDialogMode('configure');
    setShowUrlDialog(true);
  }

  return (
    <>
      <div className="mt-2 grid grid-cols-1 md:grid-cols-[200px_1fr] gap-x-10 gap-y-4 text-sm">
        <WhatsAppProfilePhoto channel={channel} canEdit={canEdit} onChanged={onProfilePhotoChanged} />

        {/* Billable usage (AIT-184: the label reflects the billing count, not
            raw activity; AIT-436: named after the org's own meter) */}
        <span className="flex items-center gap-1 text-muted-foreground">
          Billable {unitTitle(usage?.usageUnit)}
          <Tooltip>
            <TooltipTrigger
              render={
                <button
                  type="button"
                  aria-label="What counts toward my plan?"
                  className="inline-flex items-center justify-center leading-none p-0 text-muted-foreground/60 hover:text-muted-foreground"
                />
              }
            >
              <Info className="size-3.5" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              {usage?.usageUnit === 'actions'
                ? 'Actions this channel used this quota period. An action is work your app performs: sending a message, replying to a comment, moderating a comment, or publishing media. Messages you receive are free.'
                : "Messages that count toward your plan this quota period: inbound messages delivered to your endpoint and outbound messages sent. Received messages we couldn't deliver are not billed."}
            </TooltipContent>
          </Tooltip>
        </span>
        <span className="flex flex-wrap items-baseline gap-x-2">
          <span className="font-medium tabular-nums">
            {(channelUsage?.total ?? 0).toLocaleString()}{' '}
            {singularUnit(usage?.usageUnit, channelUsage?.total ?? 0)}
          </span>
        </span>

        {/* Phone Number ID */}
        {currentPhoneNumberId && (
          <>
            <span className="text-muted-foreground">Phone Number ID</span>
            <span className="flex items-center gap-1">
              <span>{currentPhoneNumberId}</span>
              <CopyButton copied={copiedPhoneId} onClick={handleCopyPhoneId} label="Copy Phone Number ID" />
            </span>
          </>
        )}

        {/* Access Token — gateway bearer (hmat_live_…), not the raw Meta token. */}
        <AccessTokenField channelId={channel.id} canEdit={canEdit} />

        {/* Channel ID */}
        <span className="flex items-center gap-1 text-muted-foreground">
          <span>Channel ID</span>
          <Tooltip>
            <TooltipTrigger
              render={
                <button
                  type="button"
                  aria-label="What is a Channel ID?"
                  className="inline-flex items-center justify-center leading-none p-0 text-muted-foreground/60 hover:text-muted-foreground"
                />
              }
            >
              <Info className="size-3.5" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              Your HookMyApp Channel ID. Use it with the CLI and when
              contacting support.
            </TooltipContent>
          </Tooltip>
        </span>
        <span className="flex items-center gap-1">
          <span>{channel.id}</span>
          <CopyButton
            copied={copiedWaba}
            onClick={handleCopyChannelId}
            label="Copy Channel ID"
          />
        </span>

        {/* Inbound Destination (Webhook URL OR HookMyApp CLI) */}
        <span className="flex items-center gap-1 text-muted-foreground">
          <span>Inbound Destination</span>
          <Tooltip>
            <TooltipTrigger
              render={
                <button
                  type="button"
                  aria-label="About inbound destination"
                  className="inline-flex items-center justify-center leading-none p-0 text-muted-foreground/60 hover:text-muted-foreground"
                />
              }
            >
              <Info className="size-3.5" />
            </TooltipTrigger>
            <TooltipContent className="block max-w-xs">
              Where inbound events are delivered. Either your own
              webhook URL or the HookMyApp CLI.{' '}
              <a
                href={docsUrl('whatsapp/receive-webhooks')}
                target="_blank"
                rel="noreferrer"
                className="underline"
              >
                Learn more
              </a>
            </TooltipContent>
          </Tooltip>
        </span>
        {!currentWebhookUrl ? (
          <span
            role="status"
            aria-label="HookMyAppCLI"
            className="flex items-center gap-2 flex-wrap"
          >
            <span className="text-sm font-medium">HookMyApp CLI</span>
            <Tooltip>
              <TooltipTrigger
                render={
                  <button
                    type="button"
                    aria-label="About the HookMyApp CLI destination"
                    className="inline-flex text-muted-foreground hover:text-foreground"
                  />
                }
              >
                <Info className="size-3.5" />
              </TooltipTrigger>
              <TooltipContent className="block max-w-xs">
                You're set up to receive events with the HookMyApp CLI.
                Click the edit icon anytime to switch to your own webhook
                URL.{' '}
                <a
                  href={docsUrl('build-with-ai/cli')}
                  target="_blank"
                  rel="noreferrer"
                  className="underline"
                >
                  Learn more
                </a>
              </TooltipContent>
            </Tooltip>
            {canEdit && (
              <Button
                variant="ghost"
                size="icon-xs"
                onClick={handleSetUrl}
                aria-label="Configure webhook URL"
                title="Configure webhook URL"
              >
                <Pencil className="size-3.5" />
              </Button>
            )}
          </span>
        ) : (
          <span className="flex items-center gap-1 min-w-0">
            <span
              className="font-mono text-xs"
              title={currentWebhookUrl}
            >
              {midEllipsisUrl(currentWebhookUrl)}
            </span>
            <CopyButton
              copied={copiedField === 'webhookUrl'}
              onClick={() => handleCopyField(currentWebhookUrl, 'webhookUrl')}
              label="Copy webhook URL"
            />
            {canEdit && (
              <Button
                variant="ghost"
                size="icon-xs"
                onClick={handleEditUrl}
                aria-label="Edit webhook URL"
                title="Edit webhook URL"
              >
                <Pencil className="size-3.5" />
              </Button>
            )}
            {canEdit && (
              <Button
                variant="ghost"
                size="icon-xs"
                onClick={() => setSwitchToCliDialogOpen(true)}
                aria-label="Switch back to HookMyApp CLI"
                title="Switch back to HookMyApp CLI"
              >
                <RotateCcw className="size-3.5" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={() => window.open(currentWebhookUrl, '_blank')}
              aria-label="Open webhook URL"
              title="Open webhook URL"
            >
              <ExternalLink className="size-3.5" />
            </Button>
          </span>
        )}

        {/* Verify Token */}
        {currentWebhookUrl && currentVerifyToken && (
          <>
            <span className="text-muted-foreground">Verify Token</span>
            <span className="flex items-center gap-1 min-w-0">
              <span
                className="font-mono text-sm"
                title={currentVerifyToken}
              >
                {midEllipsis(currentVerifyToken, 4, 4)}
              </span>
              <CopyButton
                copied={copiedVerifyToken}
                onClick={handleCopyVerifyToken}
                label="Copy verify token"
              />
            </span>
          </>
        )}

        {/* HMAC — the X-HookMyApp-Signature-256 signing secret. SEPARATE from the
            verify token; shown + rotatable only when a webhook is configured. */}
        {currentWebhookUrl && currentHmacSecret && (
          <>
            <span className="flex items-center gap-1 text-muted-foreground">
              HMAC
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button variant="ghost" size="icon-xs" aria-label="What is the HMAC secret">
                      <Info className="size-3.5" />
                    </Button>
                  }
                />
                <TooltipContent className="max-w-xs">
                  We sign every forwarded webhook with the header
                  X-HookMyApp-Signature-256 = sha256=HMAC-SHA256(this secret, raw
                  request body). Recompute it on your endpoint to confirm the
                  webhook is from us. This is not the verify token.
                </TooltipContent>
              </Tooltip>
            </span>
            <span className="flex items-center gap-1 min-w-0">
              <span className="font-mono text-sm" title={currentHmacSecret}>
                {midEllipsis(currentHmacSecret, 4, 4)}
              </span>
              <CopyButton
                copied={copiedField === 'hmac'}
                onClick={() => handleCopyField(currentHmacSecret, 'hmac')}
                label="Copy HMAC secret"
              />
              {canEdit && (
                <Button
                  variant="ghost"
                  size="icon-xs"
                  onClick={onRotateHmac}
                  disabled={rotatingHmac}
                  aria-label="Rotate HMAC secret"
                  title="Rotate HMAC secret"
                >
                  <RotateCcw className="size-3.5" />
                </Button>
              )}
            </span>
          </>
        )}
      </div>

      <div className="border-t my-2" />

      <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-x-10 gap-y-3 text-sm">
        {/* Meta Business Portfolio */}
        <span className="text-muted-foreground">Meta Business Portfolio</span>
        <span className="flex items-center gap-1">
          <span>{currentBusinessName || 'N/A'}</span>
          {currentBusinessName && (
            <CopyButton
              copied={copiedField === 'businessName'}
              onClick={() => handleCopyField(currentBusinessName, 'businessName')}
              label="Copy business name"
            />
          )}
        </span>

        {/* WhatsApp Business Name */}
        <span className="text-muted-foreground">WhatsApp Business Name</span>
        <span className="flex items-center gap-1">
          <span>{currentWabaName || 'N/A'}</span>
          {currentWabaName && (
            <CopyButton
              copied={copiedField === 'wabaName'}
              onClick={() => handleCopyField(currentWabaName, 'wabaName')}
              label="Copy WABA name"
            />
          )}
        </span>

        {/* WABA ID */}
        <span className="flex items-center gap-1 text-muted-foreground">
          <span>WABA ID</span>
          <Tooltip>
            <TooltipTrigger
              render={
                <button
                  type="button"
                  aria-label="What is a WABA ID?"
                  className="inline-flex items-center justify-center leading-none p-0 text-muted-foreground/60 hover:text-muted-foreground"
                />
              }
            >
              <Info className="size-3.5" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              Your WhatsApp Business Account ID at Meta. Use it to find
              this account in Meta Business Manager.
            </TooltipContent>
          </Tooltip>
        </span>
        <span className="flex items-center gap-1">
          <span>{channel.metaWabaId}</span>
          <CopyButton
            copied={copiedField === 'wabaId'}
            onClick={() => handleCopyField(channel.metaWabaId ?? '', 'wabaId')}
            label="Copy WABA ID"
          />
        </span>

        {/* Phone Number */}
        <span className="text-muted-foreground">Phone Number</span>
        <span className="flex items-center gap-1">
          <span>{channel.whatsappDisplayPhoneNumber || 'N/A'}</span>
          {channel.whatsappDisplayPhoneNumber && (
            <CopyButton
              copied={copiedField === 'phone'}
              onClick={() => handleCopyField(channel.whatsappDisplayPhoneNumber!.replace(/[^0-9+]/g, ''), 'phone')}
              label="Copy phone number"
            />
          )}
        </span>

        {/* Connection Type */}
        <span className="text-muted-foreground">Connection Type</span>
        <span>
          {channel.connectionType === 'coexistence' ? 'Coexistence' : 'Cloud API'}
        </span>
      </div>
      {/* Quality Rating now renders in ChannelCardShell, below the toggle rows. */}
    </>
  );
}
